import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Download, FileText, Search, Filter } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

export default function AbnormalReport() {
  const [periodType, setPeriodType] = useState("monthly");
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    Promise.all([api.get("/suppliers"), api.get("/materials")]).then(([s, m]) => {
      setSuppliers(s.data); setMaterials(m.data);
    });
  }, []);

  const buildParams = () => {
    const p = new URLSearchParams({ period_type: periodType });
    if (periodType === "monthly") { p.append("year", year); p.append("month", month); }
    if (periodType === "weekly") {
      if (startDate) p.append("start_date", startDate);
      if (endDate) p.append("end_date", endDate);
    }
    if (status) p.append("status", status);
    if (supplierId) p.append("supplier_id", supplierId);
    if (materialId) p.append("material_id", materialId);
    return p.toString();
  };

  const load = async () => {
    try {
      const r = await api.get(`/reports/abnormal?${buildParams()}`);
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periodType, year, month, startDate, endDate, status, supplierId, materialId]);

  const exportTo = async (kind) => {
    try {
      const url = `/reports/abnormal.${kind}?${buildParams()}`;
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: kind === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `abnormal_${periodType}_${year || ""}_${month || ""}.${kind}`;
      a.click();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Export failed`); }
  };

  const filtered = useMemo(() => {
    if (!q) return rows;
    const k = q.toLowerCase();
    return rows.filter((r) =>
      (r.material_code || "").toLowerCase().includes(k) ||
      (r.material_name || "").toLowerCase().includes(k) ||
      (r.supplier_code || "").toLowerCase().includes(k) ||
      (r.supplier_name || "").toLowerCase().includes(k) ||
      (r.schedule_no || "").toLowerCase().includes(k)
    );
  }, [rows, q]);

  const yrs = []; for (let y = today.getFullYear() - 1; y <= today.getFullYear() + 2; y++) yrs.push(y);

  return (
    <div data-testid="abnormal-report-page">
      <PageHeader title="Abnormal Report" subtitle="Short / Excess Quantity vs Schedule"
        helpText="Compares received GRN qty (status: stock_in) against the corresponding monthly or weekly Schedule. Short = scheduled but not received. Excess = received above scheduled."
        right={
          <div className="flex gap-2">
            <button onClick={() => exportTo("xlsx")} data-testid="export-xlsx-btn" className="bg-[#2E7D32] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-2"><Download size={12}/> Excel</button>
            <button onClick={() => exportTo("pdf")} data-testid="export-pdf-btn" className="bg-[#C62828] text-white text-xs px-3 py-2 rounded-sm uppercase tracking-widest font-bold flex items-center gap-2"><FileText size={12}/> PDF</button>
          </div>
        }
      />
      <div className="p-8 space-y-4">
        {/* Filters */}
        <div className="bg-white border border-[#EEEEEE] rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60">
            <Filter size={12}/> Filters
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Period</label>
              <select value={periodType} onChange={(e) => setPeriodType(e.target.value)} data-testid="ar-period" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="monthly">Monthly</option><option value="weekly">Weekly</option>
              </select>
            </div>
            {periodType === "monthly" ? (
              <>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Month</label>
                  <select value={month} onChange={(e) => setMonth(+e.target.value)} data-testid="ar-month" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                    {MONTHS.slice(1).map((nm, i) => <option key={i + 1} value={i + 1}>{nm}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Year</label>
                  <select value={year} onChange={(e) => setYear(+e.target.value)} data-testid="ar-year" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                    {yrs.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Start Date</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="ar-start" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">End Date</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="ar-end" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
                </div>
              </>
            )}
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ar-status" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">All</option>
                <option value="short">Short</option>
                <option value="excess">Excess</option>
                <option value="on_plan">On Plan</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Supplier</label>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="ar-supplier" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">All</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Material</label>
              <select value={materialId} onChange={(e) => setMaterialId(e.target.value)} data-testid="ar-material" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">All</option>
                {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
              </select>
            </div>
            <div className="col-span-2 relative">
              <label className="block text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 font-bold mb-1">Search</label>
              <Search size={14} className="absolute left-3 bottom-2.5 text-[#0D2B5E]/40"/>
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ar-search" placeholder="Material / supplier / schedule…" className="w-full pl-9 pr-3 py-2 border border-[#EEEEEE] rounded-sm text-sm"/>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Stat label="Short" value={filtered.filter((r) => r.status === "short").length} color="#C62828"/>
          <Stat label="Excess" value={filtered.filter((r) => r.status === "excess").length} color="#E65100"/>
          <Stat label="On Plan" value={filtered.filter((r) => r.status === "on_plan").length} color="#2E7D32"/>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: "10%" }}/><col style={{ width: "13%" }}/><col style={{ width: "16%" }}/>
              <col style={{ width: "21%" }}/><col style={{ width: "8%" }}/><col style={{ width: "8%" }}/>
              <col style={{ width: "8%" }}/><col style={{ width: "8%" }}/><col style={{ width: "8%" }}/>
            </colgroup>
            <thead className="bg-[#0D2B5E] text-white text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-3 py-2.5">Schedule</th>
                <th className="text-left px-3 py-2.5">Period</th>
                <th className="text-left px-3 py-2.5">Supplier</th>
                <th className="text-left px-3 py-2.5">Material</th>
                <th className="text-right px-3 py-2.5">Sched</th>
                <th className="text-right px-3 py-2.5">Recv</th>
                <th className="text-right px-3 py-2.5">Short</th>
                <th className="text-right px-3 py-2.5">Excess</th>
                <th className="text-left px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i} data-testid={`abnormal-row-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-3 py-2 font-mono text-[#1565C0] truncate">{r.schedule_no}</td>
                  <td className="px-3 py-2 text-xs truncate" title={r.period}>{r.period}</td>
                  <td className="px-3 py-2 truncate text-xs">{r.supplier_name}</td>
                  <td className="px-3 py-2 truncate text-xs"><span className="font-mono text-[#1565C0]">{r.material_code}</span> {r.material_name}</td>
                  <td className="px-3 py-2 font-mono text-right">{r.scheduled_qty}</td>
                  <td className="px-3 py-2 font-mono text-right">{r.received_qty}</td>
                  <td className="px-3 py-2 font-mono text-right" style={{ color: r.short_qty > 0 ? "#C62828" : "#0D2B5E" }}>{r.short_qty}</td>
                  <td className="px-3 py-2 font-mono text-right" style={{ color: r.excess_qty > 0 ? "#E65100" : "#0D2B5E" }}>{r.excess_qty}</td>
                  <td className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold" style={{ color: r.status === "short" ? "#C62828" : r.status === "excess" ? "#E65100" : "#2E7D32" }}>{r.status.replace("_", " ")}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-[#0D2B5E]/50">No data for this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white border border-[#EEEEEE] rounded-sm p-4 flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60">{label}</div>
      <div className="text-3xl font-bold font-mono" style={{ color }}>{value}</div>
    </div>
  );
}
